import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, IconButton, styled, CircularProgress
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VideoIcon from '@mui/icons-material/Movie';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { get } from '../../utility/fetchWeb';
import { motion, AnimatePresence } from 'framer-motion';

const PLACEHOLDER = {
  imageUrl: '../../Assets/images/ConnectHealthPro.png',
  title: 'Advertisement Space Available',
  subtitle: 'Contact us to promote here',
  status: 0
};

const Wrapper = styled(Box)({ position: 'relative', width: '100%' });

const BannerContainer = styled(Box)(({ theme, ispropads }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  width: '100%',
  height: ispropads ? '250px' : '580px', // Smaller height for targeted ads
  margin: '0 auto', // Center the container
  mb: 2,
  [theme.breakpoints.down('sm')]: {
    height: ispropads ? '120px' : '120px', // Consistent mobile height
  }
}));

const ArrowButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 3,
  color: theme.palette.primary.main,
  backgroundColor: 'rgba(255,255,255,0.3)',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.7)',
    color: theme.palette.primary.dark,
  }
}));

const SoundButton = styled(IconButton)(({ animate }) => ({
  color: '#fff',
  backgroundColor: 'rgba(0,0,0,0.6)',
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.8)',
  },
  position: 'absolute',
  bottom: 12,
  right: 12,
  zIndex: 3,
  ...(animate && {
    boxShadow: '0 0 0 rgba(255,255,255, 0.4)',
    animation: 'pulse-ring 1.5s infinite',
  }),
  '@keyframes pulse-ring': {
    '0%': { boxShadow: '0 0 0 0 rgba(255,255,255, 0.5)' },
    '70%': { boxShadow: '0 0 0 10px rgba(255,255,255, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(255,255,255, 0)' },
  },
}));

export default function PromoBanner({ ads: propAds, homePageAds }) {
  const [ads, setAds] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true); // Always start muted
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  // Load adverts
  useEffect(() => {
    if (propAds && propAds.length > 0) {
      // Use provided ads (targeted ads)
      setAds(propAds);
      setLoading(false);
    } else {
      // Fetch regular adverts and include homePageAds
      (async () => {
        setLoading(true);
        try {
          const res = await get('/Adverts/all-adverts');
          const list = res?.filter(a => a.status === 1) || [];
          
          // Combine regular ads with homePageAds if available
          const combinedAds = [
            ...list,
            ...(homePageAds && homePageAds.length > 0 ? homePageAds : [])
          ];
          
          setAds(combinedAds.length ? combinedAds : [PLACEHOLDER]);
        } catch {
          // If API fails, still include homePageAds if available
          const fallbackAds = homePageAds && homePageAds.length > 0 ? homePageAds : [PLACEHOLDER];
          setAds(fallbackAds);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [propAds, homePageAds]);

  // Auto-rotate (pauses if video is playing)
  const rotate = useCallback(() => {
    clearInterval(timerRef.current);
    if (ads[index]?.imageUrl.endsWith('.mp4') && playing) return;
    timerRef.current = setInterval(() => {
      setIndex(i => (i + 1) % ads.length);
    }, 7000);
  }, [ads, index, playing]);

  useEffect(() => {
    if (ads.length > 1) rotate();
    return () => clearInterval(timerRef.current);
  }, [ads, index, rotate]);

  const next = () => { setIndex(i => (i + 1) % ads.length); rotate(); };
  const prev = () => { setIndex(i => (i - 1 + ads.length) % ads.length); rotate(); };

  const onPlay = () => {
    setPlaying(true);
    clearInterval(timerRef.current);
  };

  const onEnd = () => {
    setPlaying(false);
    next();
  };

  const toggleMute = () => {
    setMuted(m => {
      const nextMute = !m;
      if (videoRef.current) videoRef.current.muted = nextMute;
      return nextMute;
    });
  };

  console.log('Current ad:', ads); // Debugging log
  // Auto-play/auto-mute based on visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const vid = videoRef.current;
        if (!vid) return;

        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          // in view: play but stay muted
          vid.muted = true;
          setMuted(true);
          vid.play().catch(() => {});
          setPlaying(true);
        } else {
          // out of view: pause
          vid.pause();
          setPlaying(false);
        }
      },
      { threshold: [0.5] } // Increased threshold
    );

    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [ads, index]);

  const current = ads[index] || PLACEHOLDER;
  const isVideo = propAds ? current.imageUrl?.endsWith('.mp4') : current.imageUrl?.endsWith('.mp4');

  const handleBannerClick = (banner) => {
    if (banner.redirectUrl) {
      // Open redirect URL in new tab
      window.open(banner.redirectUrl, '_blank', 'noopener,noreferrer');
      
      // Optional: Track click analytics
      if (banner.isTargeted) {
        console.log('Targeted ad clicked:', banner.id);
        // You can add analytics tracking here
      }
    }
  };

  return (
    <Box>
      {ads.length > 0 && <Typography variant="h6" mb={1}>
        {propAds ? 'Sponsored Content' : 'Adverts'}
      </Typography>}
      <Wrapper>
        {ads.length > 1 && (
          <ArrowButton onClick={prev} sx={{ left: 8 }}>
            <ArrowBackIcon />
          </ArrowButton>
        )}
        {ads.length > 1 && (
          <ArrowButton onClick={next} sx={{ right: 8 }}>
            <ArrowForwardIcon />
          </ArrowButton>
        )}

        <BannerContainer 
          ref={containerRef}
          ispropads={propAds ? 1 : 0} // Pass prop to styled component
          onClick={() => handleBannerClick(current)}
          sx={{
            cursor: current.redirectUrl ? 'pointer' : 'default',
            '&:hover': current.redirectUrl ? {
              transform: 'scale(1.01)',
              transition: 'transform 0.2s ease-in-out'
            } : {}
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={current.imageUrl}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5 }}
              style={{ 
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {isVideo ? (
                <Box
                  component="video"
                  ref={videoRef}
                  src={current.imageUrl?.startsWith('http') ? current.imageUrl : `${process.env.REACT_APP_MEDIA_URL}${current.imageUrl}`}
                  muted={muted}
                  autoPlay
                  onPlay={onPlay}
                  onEnded={onEnd}
                  playsInline
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Box
                  component="img"
                  src={current.imageUrl?.startsWith('http') ? current.imageUrl : `${process.env.REACT_APP_MEDIA_URL}${current.imageUrl}`}
                  alt={current.title}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    opacity: loading ? 0.5 : 1,
                    transition: 'opacity 0.3s'
                  }}
                />
              )}
            </motion.div>
          </AnimatePresence>

          {isVideo && (
            <SoundButton onClick={toggleMute} animate={!muted}>
              {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
            </SoundButton>
          )}

          {/* {isVideo && (
            <Box sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', borderRadius: '50%', p: 0.5, zIndex: 2 }}>
              <VideoIcon sx={{ color: '#fff' }} />
            </Box>
          )} */}

          {ads.length > 1 && !playing && (
            <Box sx={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 1, zIndex: 2 }}>
              {ads.map((_, i) => (
                <Box key={i} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: i === index ? 'primary.main' : 'rgba(255,255,255,0.5)' }} />
              ))}
            </Box>
          )}

          {loading && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.3)', zIndex: 1 }}>
              <CircularProgress />
            </Box>
          )}
        </BannerContainer>
      </Wrapper>
    </Box>
  );
}
